import {
  LuxTypography,
  Stack,
  useTheme,
  WarningIcon,
} from '@gemini/fe-ds-core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SubtitleWrapper } from '@components/ThankYouPage/ThankYouPage.styles';
import { ApptErrorModalWrapper } from './AppointmentErrorModal.styles';
import { NoWhiteSpaceLuxSelectablePill } from '@components/Steps/Steps.styles';
import { ErrorStatusVx, ErrorStatusTab } from '@utils/enums/ErrorStatus.enum';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { PostSubmissionError } from '@utils/enums/PostSubmissionError.enum';

interface AppointmentErrorModalProps {
  statusCode: string | undefined;
  actionCode: string | undefined;
}

const AppointmentErrorModal = ({
  statusCode,
  actionCode,
}: AppointmentErrorModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const brandName = useOnlineBookingStore(state => state.brandName);

  const statusLabel = useMemo(() => {
    const getStatusLabel = (code?: string) => {
      //we're from TAB
      if (brandName) {
        switch (code) {
          // nuovi codici di errore
          case ErrorStatusTab.PENDING:
            return t('appointmentErrorPage.pending_status');
          case ErrorStatusTab.NEXT24HOURS:
            return t('appointmentErrorPage.next24hours_status');
          case ErrorStatusTab.PAST:
          case PostSubmissionError.PAST:
            return t('appointmentErrorPage.past_status');
          case ErrorStatusTab.CONFIRM:
            return t('appointmentErrorPage.confirm_status');
          case ErrorStatusTab.NOSHOW:
            return t('appointmentErrorPage.noshow_status');
          case ErrorStatusTab.CANCEL:
            return t('appointmentErrorPage.cancel_status');
          case ErrorStatusTab.COMPLETE:
            return t('appointmentErrorPage.complete_status');
          case ErrorStatusTab.OTHER:
            return t('appointmentErrorPage.other_status');
          default:
            return code || '';
        }
      } else {
        switch (code) {
          // vecchi codici
          case ErrorStatusVx.ARRIVED:
            return t('appointmentErrorPage.arrived_status');
          case ErrorStatusVx.FULFILLED:
            return t('appointmentErrorPage.fulfilled_status');
          case ErrorStatusVx.CANCELLED:
            return t('appointmentErrorPage.cancelled_status');
          case ErrorStatusVx.NO_SHOW:
            return t('appointmentErrorPage.no_show_status');
          case ErrorStatusVx.CHECKED_IN:
            return t('appointmentErrorPage.checked_in_status');
          case PostSubmissionError.PAST:
            return t('appointmentErrorPage.past_status');
          default:
            return code || '';
        }
      }
    };
    return getStatusLabel(statusCode);
  }, [statusCode]);

  const actionLabel = useMemo(() => {
    if (!actionCode) return '';
    if (actionCode.toLowerCase() === 'confirm')
      return t('appointmentErrorPage.action1');
    if (actionCode.toLowerCase() === 'cancel')
      return t('appointmentErrorPage.action2');
    if (actionCode.toLowerCase() === 'reschedule')
      return t('appointmentErrorPage.action3');
    return '';
  }, [actionCode]);

  return (
    <>
      <ApptErrorModalWrapper
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Stack
          style={{
            borderRadius: '1rem',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            padding: '4rem 1rem',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Stack direction="row" gap="1rem" alignItems="center">
            <Stack
              style={{
                width: '2.75rem',
                height: '2.75rem',
                borderRadius: '100%',
                border: `1px solid ${theme.palette.LUX.obTokens.bordersColorFunctionalError}`,
                backgroundColor:
                  theme.palette.LUX.obTokens.bgColorFunctionalError,
              }}
              justifyContent="center"
              alignItems="center"
            >
              <WarningIcon size="medium" />
            </Stack>
            <LuxTypography variant="heading3">
              {t('appointmentErrorPage.attention')}
            </LuxTypography>
          </Stack>
          <SubtitleWrapper>
            <LuxTypography variant="textMdRegular">
              {t('appointmentErrorPage.encountProblem')}
            </LuxTypography>
          </SubtitleWrapper>
          <NoWhiteSpaceLuxSelectablePill
            iconStart="WarningIcon"
            color="error"
            selectable={false}
            text={t('appointmentErrorPage.manageOrRescheduleError')
              ?.replace('{{ status }}', statusLabel)
              ?.replace('{{ action }}', actionLabel)}
            style={{
              width: '100%',
              maxWidth: 'fit-content',
              alignSelf: 'center',
            }}
          />
        </Stack>
      </ApptErrorModalWrapper>
    </>
  );
};

export default AppointmentErrorModal;
